import { FC, useCallback, useEffect, useMemo } from 'react';
import { CallIntelConstant, CallIntelTypes } from '@frontend/api-call-intel';
import { DropDownFilterSelector } from '@frontend/analytics-shared';
import { useOptionsProvider } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { trackingIds } from '../../../../tracking-ids';
import { FORMATTED_OFFICE_HOURS_SUPPORT_DATE } from '../../constants';
import { useCallIntelFiltersContext } from '../context/call-intel-filters.context';
import { getOfficeHoursDateValidation } from '../utils';
import { FilterTraySection } from './filter-tray-section/filter-tray-section';

export interface OfficeHoursFilterTrayProps {
  disabled?: boolean;
  showClear?: boolean;
  validateCallDirection?: boolean;
}

export const OfficeHoursFilterTray: FC<OfficeHoursFilterTrayProps> = ({
  disabled,
  showClear = true,
  validateCallDirection = false,
}) => {
  const { t } = useTranslation('call-intelligence');
  const { filterValues, isLoading, updateFilterValues, updateFilterModified, updateErrors } =
    useCallIntelFiltersContext();
  const { getOptions } = useOptionsProvider();

  const { isValid } = useMemo(
    () => getOfficeHoursDateValidation(filterValues.startDate, filterValues.endDate),
    [filterValues.startDate, filterValues.endDate]
  );

  const hasOfficeHoursSelected = !!filterValues.officeHours?.length;
  const isOutboundSelected =
    validateCallDirection && filterValues.callDirection === CallIntelTypes.CallDirectionEnum.DIRECTION_OUTBOUND;
  const isDateInvalid = hasOfficeHoursSelected && !isValid;
  const isOutboundInvalid = hasOfficeHoursSelected && isOutboundSelected;
  const isDisabled = disabled || isLoading;

  const filterValue = useMemo(() => {
    const selected = filterValues.officeHours ?? [];
    if (
      selected.length === CallIntelConstant.ALL_OFFICE_HOURS_VALUES.length &&
      CallIntelConstant.ALL_OFFICE_HOURS_VALUES.every((v) => selected.includes(v))
    ) {
      return CallIntelConstant.ALL_OFFICE_HOURS_ID;
    }
    return selected[0];
  }, [filterValues.officeHours]);

  const onChange = useCallback(
    (value: string) => {
      if (!value) {
        updateFilterValues({ officeHours: [] });
      } else if (value === CallIntelConstant.ALL_OFFICE_HOURS_ID) {
        updateFilterValues({ officeHours: CallIntelConstant.ALL_OFFICE_HOURS_VALUES });
      } else {
        updateFilterValues({ officeHours: [value as CallIntelTypes.OfficeHoursEnum] });
      }
    },
    [updateFilterValues]
  );

  const onClear = useCallback(() => {
    updateFilterValues({ officeHours: [] });
  }, [updateFilterValues]);

  useEffect(() => {
    updateFilterModified({ officeHours: hasOfficeHoursSelected });
  }, [hasOfficeHoursSelected, updateFilterModified]);

  useEffect(() => {
    if (isDateInvalid) {
      updateErrors({
        officeHours: t(
          'Office Hours filtering is available starting {{date}}. Select a date range after this date to use this filter.',
          { date: FORMATTED_OFFICE_HOURS_SUPPORT_DATE }
        ),
      });
    } else if (isOutboundInvalid) {
      updateErrors({
        officeHours: t('Office Hours only apply to Inbound calls. Select Inbound calls to use this filter.'),
      });
    } else {
      updateErrors({ officeHours: false });
    }
  }, [isDateInvalid, isOutboundInvalid, updateErrors, t]);

  return (
    <FilterTraySection label={t('Office Hours')} disableClear={isDisabled} showClear={showClear} onClear={onClear}>
      <DropDownFilterSelector
        label={t('Select Office Hours')}
        onChange={onChange}
        options={getOptions('officeHours', 'array')}
        value={filterValue}
        trackingId={trackingIds.callIntel.officeHourFilter}
      />
    </FilterTraySection>
  );
};
